import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Typography } from "@smartfaster-ui/components";

type ErrorBannerProps = {
  /**
   * Error code from the generation pipeline (e.g. "image_model_failed",
   * "vocab_version_mismatch", "generation_in_flight", "request_failed")
   */
  errorCode: string | null;
  /**
   * Dispatches GENERATION_RETRIED to replay the last failed request
   */
  onRetry: () => void;
  /**
   * Resets generation state to idle (dismisses the banner)
   */
  onDismiss: () => void;
};

const ERROR_CODE_TO_I18N_KEY: Record<string, string> = {
  vocab_version_mismatch: "interiorDesigner.error.vocabMismatch",
  generation_in_flight: "interiorDesigner.error.inFlight",
  image_model_failed: "interiorDesigner.error.modelFailed",
  model_timeout: "interiorDesigner.error.modelTimeout",
  internal: "interiorDesigner.error.generic",
  request_failed: "interiorDesigner.error.generic",
};

const NON_RETRYABLE_CODES = new Set([
  "vocab_version_mismatch",
  "generation_in_flight",
]);

/**
 * Error banner shown when AI generation fails.
 * Rendered as a Stack child (grid-stacked over Canvas).
 * Non-retryable errors (vocab mismatch, in-flight) only show dismiss.
 */
export const ErrorBanner: FC<ErrorBannerProps> = ({
  errorCode,
  onRetry,
  onDismiss,
}) => {
  const { t } = useTranslation();

  const i18nKey =
    ERROR_CODE_TO_I18N_KEY[errorCode ?? ""] ?? "interiorDesigner.error.generic";
  const canRetry = !NON_RETRYABLE_CODES.has(errorCode ?? "");

  return (
    <Flex
      width="fill"
      height="fill"
      direction="column"
      align="center"
      justify="center"
      gap="sm"
      padding="lg"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <Typography variant="bodySm" color="foregroundInverse" align="center">
        {t(i18nKey)}
      </Typography>
      <Flex gap="xs">
        {canRetry && (
          <Button variant="filled" size="sm" color="primary" onClick={onRetry}>
            {t("interiorDesigner.error.retry")}
          </Button>
        )}
        <Button
          variant="outlined"
          size="sm"
          color="surfaceNeutralSecondary"
          onClick={onDismiss}
        >
          {t("interiorDesigner.error.dismiss")}
        </Button>
      </Flex>
    </Flex>
  );
};
